import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { 
  FaBan, 
  FaMoneyBillWave, 
  FaCoins, 
  FaTag, 
  FaCrown, 
  FaTicketAlt, 
  FaFileInvoice,
  FaClock,
  FaPrint,
  FaExchangeAlt,
  FaCreditCard
} from 'react-icons/fa';
import moment from 'moment';
import 'moment/locale/es';
import { getReceiptActions } from '../../../../../../../../../../crud/payments_api/payment_receipts';
import './ActionHistory.scss';

// Types matching backend response
export type ReceiptActionType = 
  | 'cancel'
  | 'refund'
  | 'tip_update'
  | 'discount_update'
  | 'membership_update'
  | 'coupon_update'
  | 'dte_creation'
  | 'table_move'
  | 'print'
  | 'transaction_update';

export interface ReceiptAction {
  action_type: ReceiptActionType;
  user_id: number;
  user_name: string;
  timestamp: string;
  description: string;
  comment?: string;
}

export interface GetReceiptActionsResponse {
  actions: ReceiptAction[];
}

interface ActionHistoryProps {
  receiptId: number;
}

const ActionHistory: React.FC<ActionHistoryProps> = ({ receiptId }) => {
  const [actions, setActions] = useState<ReceiptAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Configure Spanish locale for moment
  useEffect(() => {
    moment.locale('es');
  }, []);

  useEffect(() => {
    if (!receiptId) return;

    const fetchActions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await getReceiptActions(receiptId);
        const data: GetReceiptActionsResponse = response.data;
        // Reverse the array to show newest actions first
        setActions((data.actions || []).reverse());
      } catch (err: any) {
        console.error('Error fetching receipt actions:', err);
        setError('Error al cargar el historial de acciones');
      } finally {
        setLoading(false);
      }
    };

    fetchActions();
  }, [receiptId]);

  const getActionIcon = (actionType: ReceiptActionType) => {
    switch (actionType) {
      case 'cancel':
        return <FaBan className="action-icon cancel" />;
      case 'refund':
        return <FaMoneyBillWave className="action-icon refund" />;
      case 'tip_update':
        return <FaCoins className="action-icon tip" />;
      case 'discount_update':
        return <FaTag className="action-icon discount" />;
      case 'membership_update':
        return <FaCrown className="action-icon membership" />;
      case 'coupon_update':
        return <FaTicketAlt className="action-icon coupon" />;
      case 'dte_creation':
        return <FaFileInvoice className="action-icon dte" />;
      case 'table_move':
        return <FaExchangeAlt className="action-icon table-move" />;
      case 'print':
        return <FaPrint className="action-icon print" />;
      case 'transaction_update':
        return <FaCreditCard className="action-icon transaction-update" />;
      default:
        return <FaClock className="action-icon default" />;
    }
  };

  const getActionTypeLabel = (actionType: ReceiptActionType): string => {
    switch (actionType) {
      case 'cancel':
        return 'Cancelación';
      case 'refund':
        return 'Devolución';
      case 'tip_update':
        return 'Actualización de Propina';
      case 'discount_update':
        return 'Actualización de Descuento';
      case 'membership_update':
        return 'Membresía Aplicada';
      case 'coupon_update':
        return 'Cupón Aplicado';
      case 'dte_creation':
        return 'Documento Tributario';
      case 'table_move':
        return 'Cambio de Mesa';
      case 'print':
        return 'Impresión';
      case 'transaction_update':
        return 'Cambio de Método de Pago';
      default:
        return 'Acción';
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    return moment(timestamp).format('DD/MM/YYYY HH:mm');
  };

  const getRelativeTime = (timestamp: string): string => {
    return moment(timestamp).fromNow();
  };

  if (loading) {
    return (
      <div className="action-history-loading">
        <Spinner animation="border" variant="primary" />
        <p>Cargando historial...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="action-history-error">
        <p>{error}</p>
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="action-history-empty">
        <FaClock className="empty-icon" />
        <h4>Sin Acciones Registradas</h4>
        <p>No se han registrado acciones para este recibo.</p>
      </div>
    );
  }

  return (
    <div className="action-history-container">
      <div className="action-history-header">
        <h4>Historial de Acciones</h4>
        <span className="action-count">{actions.length} {actions.length === 1 ? 'acción' : 'acciones'}</span>
      </div>

      <div className="action-timeline">
        {actions.map((action, index) => (
          <div key={index} className={`action-item ${action.action_type}`}>
            <div className="action-icon-wrapper">
              {getActionIcon(action.action_type)}
              {index < actions.length - 1 && <div className="timeline-line" />}
            </div>

            <div className="action-content">
              <div className="action-header">
                <span className="action-type-label">{getActionTypeLabel(action.action_type)}</span>
                <span className="action-time" title={formatTimestamp(action.timestamp)}>
                  {getRelativeTime(action.timestamp)}
                </span>
              </div>

              <div className="action-description">
                {action.description}
              </div>

              {action.comment && (
                <div className="action-comment">
                  <span className="comment-label">Nota:</span> {action.comment}
                </div>
              )}

              <div className="action-footer">
                <span className="action-user">
                  <FaClock className="user-icon" /> {action.user_name}
                </span>
                <span className="action-timestamp">{formatTimestamp(action.timestamp)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActionHistory;
